import React, { useState, useEffect } from 'react';
import api from '../../utils/api';

export default function PlansPage() {
  const [plans, setPlans]       = useState([]);
  const [loading, setLoading]   = useState(true);
  const [editing, setEditing]   = useState(null);
  const [form, setForm]         = useState({});
  const [saving, setSaving]     = useState(false);
  const [toggling, setToggling] = useState(null); // id del plan en proceso
  const [success, setSuccess]   = useState('');
  const [error, setError]       = useState('');

  useEffect(() => { load(); }, []);

  const load = async () => {
    try {
      const res = await api.get('/plans/todos');
      setPlans(res.data);
    } catch { setError('Error cargando planes'); }
    finally { setLoading(false); }
  };

  const notify = (msg, isError = false) => {
    if (isError) { setError(msg); setTimeout(() => setError(''), 4000); }
    else          { setSuccess(msg); setTimeout(() => setSuccess(''), 4000); }
  };

  const startEdit = (plan) => { setEditing(plan.id); setForm(plan); };

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/plans/${editing}`, form);
      notify('Plan actualizado correctamente');
      setEditing(null);
      load();
    } catch { notify('Error guardando plan', true); }
    finally { setSaving(false); }
  };

  const togglePlan = async (plan) => {
    const accion = plan.activo ? 'desactivar' : 'activar';
    if (!window.confirm(
      `¿Confirmas ${accion} el plan "${plan.nombre_visible}"?\n\n` +
      `Todos los usuarios de negocios con este plan también serán ${plan.activo ? 'desactivados' : 'activados'}.`
    )) return;

    setToggling(plan.id);
    try {
      const res = await api.patch(`/plans/${plan.id}/toggle`);
      notify(res.data.message);
      load();
    } catch (err) {
      notify(err.response?.data?.error || 'Error al cambiar estado del plan', true);
    } finally { setToggling(null); }
  };

  const planColor  = (name) => ({ basic: '#6366f1', medium: '#3b82f6', premium: '#4f46e5' }[name?.toLowerCase()] || '#888');
  const planIcon   = (name) => ({ basic: '🟢', medium: '🔵', premium: '🟣' }[name?.toLowerCase()] || '⚙️');

  const boolField = (key, label) => (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid var(--color-border)' }}>
      <span style={{ fontSize: '0.85rem', color: 'var(--color-text-muted)' }}>{label}</span>
      <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
        <input
          type="checkbox"
          checked={!!form[key]}
          onChange={e => setForm({ ...form, [key]: e.target.checked })}
        />
        <span style={{ fontSize: '0.8rem', color: form[key] ? 'var(--color-gold)' : 'var(--color-text-muted)' }}>
          {form[key] ? '✅ Activo' : '❌ Inactivo'}
        </span>
      </label>
    </div>
  );

  return (
    <div className="page-content">
      <div className="page-header">
        <div>
          <h1 className="page-title">Planes de Suscripción</h1>
          <p className="page-subtitle">Configuración global de planes</p>
        </div>
      </div>

      {success && <div className="alert alert-success" style={{ marginBottom: '1rem' }}>{success}</div>}
      {error   && <div className="alert alert-danger"  style={{ marginBottom: '1rem' }}>{error}</div>}

      {loading ? (
        <p style={{ color: 'var(--color-text-muted)' }}>Cargando...</p>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '1.5rem' }}>
          {plans.map(plan => {
            const inactivo = !plan.activo;
            return (
              <div
                key={plan.id}
                className="card"
                style={{
                  borderTop: `3px solid ${inactivo ? '#94a3b8' : planColor(plan.nombre)}`,
                  opacity: inactivo ? 0.65 : 1,
                  position: 'relative',
                }}
              >
                {/* Badge INACTIVO */}
                {inactivo && (
                  <div style={{
                    position: 'absolute', top: 12, right: 12,
                    background: '#ef4444', color: '#fff',
                    fontSize: '0.65rem', fontWeight: 700,
                    padding: '2px 8px', borderRadius: 99,
                    letterSpacing: '0.05em',
                  }}>
                    INACTIVO
                  </div>
                )}

                {/* Encabezado */}
                <div style={{ padding: '1.5rem', borderBottom: '1px solid var(--color-border)' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
                    <div>
                      <h3 style={{ margin: 0, color: inactivo ? '#94a3b8' : planColor(plan.nombre), fontFamily: 'var(--font-display)' }}>
                        {planIcon(plan.nombre)} {plan.nombre_visible}
                      </h3>
                      <div style={{ marginTop: '4px', fontSize: '1.5rem', fontWeight: 700, color: 'var(--color-text)' }}>
                        ${plan.precio?.toLocaleString('es-CO')}
                        <span style={{ fontSize: '0.8rem', color: 'var(--color-text-muted)', fontWeight: 400 }}>/mes</span>
                      </div>
                    </div>

                    {editing !== plan.id && (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 6, alignItems: 'flex-end' }}>
                        <button className="btn btn-ghost" style={{ fontSize: '0.8rem' }} onClick={() => startEdit(plan)}>
                          Editar
                        </button>
                        <button
                          onClick={() => togglePlan(plan)}
                          disabled={toggling === plan.id}
                          style={{
                            fontSize: '0.75rem', fontWeight: 600, padding: '4px 12px',
                            border: 'none', borderRadius: 6, cursor: 'pointer',
                            background: inactivo ? '#dcfce7' : '#fee2e2',
                            color:      inactivo ? '#15803d' : '#b91c1c',
                          }}
                        >
                          {toggling === plan.id ? '...' : inactivo ? '▶ Activar' : '⏸ Desactivar'}
                        </button>
                      </div>
                    )}
                  </div>
                </div>

                {/* Formulario de edición */}
                {editing === plan.id ? (
                  <div style={{ padding: '1.5rem' }}>
                    <div className="form-group">
                      <label className="form-label">Precio mensual</label>
                      <input type="number" className="form-input" value={form.precio || ''} onChange={e => setForm({ ...form, precio: +e.target.value })} />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Máximo de usuarios</label>
                      <input type="number" className="form-input" value={form.max_usuarios || ''} onChange={e => setForm({ ...form, max_usuarios: +e.target.value })} />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Máximo de sedes</label>
                      <input type="number" className="form-input" value={form.max_sucursales || ''} onChange={e => setForm({ ...form, max_sucursales: +e.target.value })} />
                    </div>
                    <div style={{ marginTop: '1rem' }}>
                      <div style={{ fontWeight: 600, marginBottom: '8px', fontSize: '0.85rem', color: 'var(--color-text-muted)' }}>MÓDULOS</div>
                      {boolField('tiene_trabajadores',       'Gestión de trabajadores')}
                      {boolField('tiene_reportes',           'Reportes operativos')}
                      {boolField('tiene_reportes_avanzados', 'Reportes avanzados')}
                      {boolField('tiene_finanzas',           'Módulo de finanzas')}
                      {boolField('tiene_multi_sucursal',     'Multi-sede')}
                      {boolField('tiene_dashboard',          'Dashboard')}
                    </div>
                    <div style={{ display: 'flex', gap: '0.5rem', marginTop: '1.5rem' }}>
                      <button className="btn btn-secondary" onClick={() => setEditing(null)}>Cancelar</button>
                      <button className="btn btn-primary" onClick={save} disabled={saving}>{saving ? 'Guardando...' : 'Guardar'}</button>
                    </div>
                  </div>
                ) : (
                  /* Vista de módulos */
                  <div style={{ padding: '1.5rem' }}>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem', marginBottom: '1rem' }}>
                      <div style={{ background: 'var(--color-surface)', borderRadius: '8px', padding: '0.75rem', textAlign: 'center' }}>
                        <div style={{ fontSize: '1.4rem', fontWeight: 700, color: 'var(--color-gold)' }}>{plan.max_usuarios}</div>
                        <div style={{ fontSize: '0.75rem', color: 'var(--color-text-muted)' }}>Usuarios</div>
                      </div>
                      <div style={{ background: 'var(--color-surface)', borderRadius: '8px', padding: '0.75rem', textAlign: 'center' }}>
                        <div style={{ fontSize: '1.4rem', fontWeight: 700, color: 'var(--color-gold)' }}>{plan.max_sucursales}</div>
                        <div style={{ fontSize: '0.75rem', color: 'var(--color-text-muted)' }}>Sedes</div>
                      </div>
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                      {[
                        [plan.tiene_trabajadores,       'Gestión de trabajadores'],
                        [plan.tiene_reportes,           'Reportes operativos'],
                        [plan.tiene_reportes_avanzados, 'Reportes avanzados'],
                        [plan.tiene_finanzas,           'Módulo de finanzas'],
                        [plan.tiene_multi_sucursal,     'Multi-sede'],
                        [plan.tiene_dashboard,          'Dashboard'],
                      ].map(([val, lbl], i) => (
                        <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '0.85rem', color: val ? 'var(--color-text)' : 'var(--color-text-muted)' }}>
                          <span>{val ? '✅' : '❌'}</span> {lbl}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
